import dayjs from "dayjs";
import { fromPairs } from "lodash";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
    AccordionTab,
    MessageType,
    Modal,
} from "../../../../common/components";
import { DATE_TIME_FORMAT } from "../../../../common/constants";
import { showToast } from "../../../../common/services/toast";
import { getSanitizedHtml } from "../../../../common/utils";
import { confirmDialog } from "../../../../common/utils/dialog.utils";
import { useCreateAnnouncement, useUpdateAnnouncement } from "../../api";
import {
    ANNOUNCEMENT_ERROR_CODES,
    ANNOUNCEMENT_SEVERITIES,
    ANNOUNCEMENT_STATUSES,
    ANNOUNCEMENT_TYPES,
} from "../../constants";
import { Announcement, AnnouncementPayload } from "../../models";
import { useAnnouncementsStore } from "../../store";
import { AnnouncementStatus } from "../../types";
import { getAnnouncementTranslation } from "../../utils";
import AnnouncementModalFooter from "./AnnouncementModalFooter";
import AnnouncementQuillEditor from "./AnnouncementQuillEditor";

const DEFAULT_ANNOUNCEMENT_SEVERITY = ANNOUNCEMENT_SEVERITIES.WARNING;

interface AnnouncementModalProps {
    isOpen?: boolean;
    onClickClose?: () => void;
    onCloseAnimationEnd?: () => void;
    onSuccess?: () => void;
    announcement?: Announcement | null;
}

const LANGUAGES = [
    { code: "de", labelKey: "language:languages.de" },
    { code: "en", labelKey: "language:languages.en" },
];

const TITLE_MAX = 30;
const TEXT_MAX = 150;
const CALENDAR_DATE_FORMAT = "dd.mm.yy";

type LanguageData = {
    title: string;
    text: string;
    textLength: number;
};

// Extracted from the LANGUAGES.map callback to keep AnnouncementModal readable.
// Lives in the same file because it is only used here and is too small to justify
// its own module.
interface AccordionLanguageHeaderProps {
    open: boolean;
    filled: boolean;
    label: string;
}

const AccordionLanguageHeader = (props: AccordionLanguageHeaderProps) => {
    const { open, filled, label } = props;
    const { t } = useTranslation(["announcements"]);

    return (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                <i
                    className={
                        open
                            ? PrimeIcons.CHEVRON_DOWN
                            : PrimeIcons.CHEVRON_RIGHT
                    }
                />
                <span
                    className={
                        open ? "font-semibold" : "font-medium text-default/60"
                    }
                >
                    {label}
                </span>
            </div>
            {filled ? (
                <i
                    className={`${PrimeIcons.CHECK_CIRCLE} text-success-default`}
                />
            ) : (
                <div className="flex items-center gap-1 text-sm">
                    <i className={PrimeIcons.EXCLAMATION_TRIANGLE} />
                    <span>{t("announcements:modal.notProvided")}</span>
                </div>
            )}
        </div>
    );
};

const BannerPreview = () => (
    <svg
        width="150"
        height="85"
        viewBox="0 0 150 85"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="rounded border border-surface-border shrink-0"
    >
        <rect width="150" height="85" fill="#f5f5f5" />
        <rect width="150" height="22" fill="#5a7a2e" />
        <line
            x1="136"
            y1="6"
            x2="144"
            y2="14"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <line
            x1="144"
            y1="6"
            x2="136"
            y2="14"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <rect
            x="6"
            y="10"
            width="7"
            height="7"
            fill="white"
            transform="rotate(45 9.5 13.5)"
        />
        <rect x="8" y="30" width="18" height="18" fill="#d0d0d0" rx="2" />
        <rect x="32" y="32" width="110" height="6" fill="#d0d0d0" rx="2" />
        <rect x="32" y="42" width="80" height="6" fill="#e0e0e0" rx="2" />
    </svg>
);

const getInitialLanguageData = (): Record<string, LanguageData> =>
    Object.fromEntries(
        LANGUAGES.map((lang) => [
            lang.code,
            { title: "", text: "", textLength: 0 },
        ])
    );

const ANNOUNCEMENT_ERROR_KEYS: Record<string, string> = {
    [ANNOUNCEMENT_ERROR_CODES.END_IN_PAST]:
        "toast:announcements.validation.endInPast",
    [ANNOUNCEMENT_ERROR_CODES.END_BEFORE_START]:
        "toast:announcements.validation.endBeforeStart",
    [ANNOUNCEMENT_ERROR_CODES.START_IMMUTABLE_WHEN_LIVE]:
        "toast:announcements.validation.startImmutableWhenLive",
    [ANNOUNCEMENT_ERROR_CODES.NO_TRANSLATIONS]:
        "toast:announcements.validation.noTranslations",
    [ANNOUNCEMENT_ERROR_CODES.FORBIDDEN_HTML_CONTENT]:
        "toast:announcements.validation.forbiddenHtmlContent",
    [ANNOUNCEMENT_ERROR_CODES.FORBIDDEN_STATUS]:
        "toast:announcements.validation.forbiddenStatus",
    [ANNOUNCEMENT_ERROR_CODES.ANNOUNCEMENT_ARCHIVED]:
        "toast:announcements.validation.announcementArchived",
};

const AnnouncementModal = (props: AnnouncementModalProps) => {
    const {
        isOpen = false,
        onClickClose = () => {},
        onCloseAnimationEnd,
        onSuccess,
        announcement = null,
    } = props;

    const isEditMode = !!announcement?.id;

    const { setPreviewAnnouncement } = useAnnouncementsStore();

    const { t } = useTranslation([
        "common",
        "announcements",
        "language",
        "toast",
    ]);

    const [startDateTime, setStartDateTime] = useState<Date | null>(null),
        [endDateTime, setEndDateTime] = useState<Date | null>(null),
        [expandedLangs, setExpandedLangs] = useState<Record<string, boolean>>({
            de: true,
            en: true,
        }),
        [languageData, setLanguageData] = useState<
            Record<string, LanguageData>
        >(getInitialLanguageData());

    const handleSuccess = () => {
        onSuccess?.();
        onClickClose();
    };

    const handleError = (err: unknown) => {
        const errorCode = (err as Record<string, unknown>)?.ErrorCode as string;
        const validationKey = ANNOUNCEMENT_ERROR_KEYS[errorCode];

        showToast({
            summary: validationKey
                ? t("toast:errorHeadline")
                : t("toast:announcements.saveFailed.title"),
            detail: t(
                validationKey ?? "toast:announcements.saveFailed.description"
            ),
            severity: MessageType.ERROR,
        });
    };

    const { triggerCreateAnnouncement, isLoadingCreateAnnouncement } =
        useCreateAnnouncement({
            onSuccess: handleSuccess,
            onError: handleError,
        });

    const { triggerUpdateAnnouncement, isLoadingUpdateAnnouncement } =
        useUpdateAnnouncement({
            onSuccess: handleSuccess,
            onError: handleError,
        });

    const isLoading =
        isLoadingCreateAnnouncement || isLoadingUpdateAnnouncement;

    const handleClose = () => {
        setPreviewAnnouncement(null);

        let hasChanges = false;

        if (isEditMode) {
            const hasDateChanges =
                !dayjs(startDateTime).isSame(
                    dayjs(announcement.startDateTime),
                    "minute"
                ) ||
                !dayjs(endDateTime).isSame(
                    dayjs(announcement.endDateTime),
                    "minute"
                );

            const hasTransChanges = LANGUAGES.some((lang) => {
                const initalTrans = getAnnouncementTranslation(
                    announcement.translations,
                    lang.code
                );
                return (
                    languageData[lang.code]?.title !==
                        (initalTrans?.title ?? "") ||
                    languageData[lang.code]?.text !==
                        (initalTrans?.description ?? "")
                );
            });

            hasChanges = hasDateChanges || hasTransChanges;
        } else {
            const hasDateInput = startDateTime !== null || endDateTime !== null;

            const hasLanguageInput = LANGUAGES.some(
                (lang) =>
                    languageData[lang.code]?.title !== "" ||
                    languageData[lang.code]?.textLength > 0
            );

            hasChanges = hasDateInput || hasLanguageInput;
        }

        if (hasChanges) {
            confirmDialog({
                header: t("announcements:modal.closeModal"),
                message: (
                    <Trans i18nKey="announcements:modal.closeModalQuestion" />
                ),
                contentClassName: "*:!m-0",
                acceptLabel: t("common:close") || "",
                rejectLabel: t("common:cancel") || "",
                accept: onClickClose,
            });
        } else {
            onClickClose();
        }
    };

    // Init data
    useEffect(() => {
        if (!isOpen) return;

        if (isEditMode && announcement) {
            setStartDateTime(dayjs(announcement.startDateTime).toDate());
            setEndDateTime(dayjs(announcement.endDateTime).toDate());

            setLanguageData(
                fromPairs(
                    LANGUAGES.map((lang) => {
                        const translation = getAnnouncementTranslation(
                            announcement.translations,
                            lang.code
                        );
                        return [
                            lang.code,
                            {
                                title: translation?.title ?? "",
                                text: translation?.description ?? "",
                                textLength: translation?.description
                                    ? translation.description.replace(
                                          /<[^>]*>/g,
                                          ""
                                      ).length
                                    : 0,
                            },
                        ];
                    })
                )
            );
        } else {
            setStartDateTime(null);
            setEndDateTime(null);
            setLanguageData(getInitialLanguageData());
        }
        setExpandedLangs({ de: true, en: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, announcement?.id]);

    const handleNow = () => {
        const now = dayjs().startOf("minute");
        setStartDateTime(now.toDate());
        if (endDateTime && now.isAfter(endDateTime)) {
            setEndDateTime(now.add(1, "minute").toDate());
        }
    };

    const updateTitle = (code: string, value: string) => {
        setLanguageData((prev) => ({
            ...prev,
            [code]: { ...prev[code], title: value },
        }));
    };

    const updateText = (code: string, html: string, textLength: number) => {
        setLanguageData((prev) => ({
            ...prev,
            [code]: { ...prev[code], text: html, textLength },
        }));
    };

    const handlePreview = (data: LanguageData) =>
        setPreviewAnnouncement({
            type: ANNOUNCEMENT_TYPES.BANNER,
            title: data.title,
            description: data.text,
            severity: announcement?.severity ?? DEFAULT_ANNOUNCEMENT_SEVERITY,
        });

    const isLanguageFilled = (code: string) =>
        !!languageData[code]?.title && languageData[code]?.textLength > 0;

    const filledLanguages = LANGUAGES.filter((lang) =>
        isLanguageFilled(lang.code)
    );
    const isFormIncomplete =
        !startDateTime ||
        !endDateTime ||
        filledLanguages.length < LANGUAGES.length;

    const handleSave = (status: AnnouncementStatus) => {
        if (isFormIncomplete || isLoading) return;

        const payload: AnnouncementPayload = {
            announcementType: ANNOUNCEMENT_TYPES.BANNER,
            severity: announcement?.severity ?? DEFAULT_ANNOUNCEMENT_SEVERITY,
            status,
            startDateTime: dayjs(startDateTime).format(),
            endDateTime: dayjs(endDateTime).format(),
            translations: LANGUAGES.map((lang) => ({
                languageCode: lang.code,
                title: languageData[lang.code].title,
                description: getSanitizedHtml(languageData[lang.code].text),
            })),
        };

        if (isEditMode) {
            triggerUpdateAnnouncement({
                announcementId: announcement.id,
                body: payload,
            });
        } else {
            triggerCreateAnnouncement({ body: payload });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            title={
                isEditMode
                    ? t("announcements:modal.edit.title")
                    : t("announcements:modal.create.title")
            }
            onClickClose={handleClose}
            onClickCloseAnimationEnd={onCloseAnimationEnd}
            footer={
                <AnnouncementModalFooter
                    isFormIncomplete={isFormIncomplete}
                    isLoading={isLoading}
                    announcementStatus={announcement?.status}
                    onClickClose={handleClose}
                    onSaveDraft={() => handleSave(ANNOUNCEMENT_STATUSES.DRAFT)}
                    onActivate={() => handleSave(ANNOUNCEMENT_STATUSES.ACTIVE)}
                />
            }
            className="max-w-4xl"
        >
            {/* Banner section */}
            <div className="flex items-start gap-4 mb-6">
                <BannerPreview />
                <div>
                    <p className="font-semibold">
                        {t("announcements:banner.title")}
                    </p>
                    <p>{t("announcements:banner.description")}</p>
                </div>
            </div>

            {/* Date / Time section */}
            <div className="flex flex-col sm:flex-row gap-8 mb-6">
                {/* Start */}
                <div className="flex-1">
                    <div className="flex gap-2 items-end mb-2">
                        <div className="w-full sm:w-[15rem]">
                            <label className="font-medium mb-1">
                                {t("announcements:modal.startDate")}
                                <span className="text-danger-default">*</span>
                            </label>
                            <Calendar
                                value={startDateTime}
                                onChange={(e) =>
                                    setStartDateTime(e.value ?? null)
                                }
                                placeholder={DATE_TIME_FORMAT}
                                dateFormat={CALENDAR_DATE_FORMAT}
                                showIcon
                                showTime
                                className="w-full"
                                maxDate={
                                    endDateTime
                                        ? dayjs(endDateTime)
                                              .subtract(1, "minute")
                                              .toDate()
                                        : undefined
                                }
                            />
                        </div>
                        <Button
                            // @ts-ignore
                            label={t("announcements:modal.now")}
                            onClick={handleNow}
                            className="h-12"
                        />
                    </div>
                    <p className="text-md text-default/60">
                        {t("announcements:modal.startDateHelper")}
                    </p>
                </div>

                {/* End */}
                <div className="flex-1">
                    <div className="w-full sm:w-[15rem] mb-2">
                        <label className="block font-medium mb-1">
                            {t("announcements:modal.endDate")}
                            <span className="text-danger-default">*</span>
                        </label>
                        <Calendar
                            value={endDateTime}
                            onChange={(e) => setEndDateTime(e.value ?? null)}
                            placeholder={DATE_TIME_FORMAT}
                            dateFormat={CALENDAR_DATE_FORMAT}
                            showIcon
                            showTime
                            className="w-full"
                            minDate={
                                startDateTime
                                    ? dayjs(startDateTime)
                                          .add(1, "minute")
                                          .toDate()
                                    : undefined
                            }
                        />
                    </div>
                    <p className="text-md text-default/60 max-w-72">
                        {t("announcements:modal.endDateHelper")}
                    </p>
                </div>
            </div>

            {/* Language accordions */}
            <div className="flex flex-col gap-3">
                {LANGUAGES.map((lang) => {
                    const isFilled = isLanguageFilled(lang.code);
                    const data = languageData[lang.code];
                    const open = expandedLangs[lang.code];

                    return (
                        <AccordionTab
                            key={lang.code}
                            header={
                                <AccordionLanguageHeader
                                    open={open}
                                    filled={isFilled}
                                    label={t(lang.labelKey)}
                                />
                            }
                            isOpen={open}
                            onToggle={(val) =>
                                setExpandedLangs((prev) => ({
                                    ...prev,
                                    [lang.code]: val,
                                }))
                            }
                            headerClassName="bg-surface-3 border-surface-6"
                        >
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {t("announcements:modal.titleLabel")}
                                    <span className="text-danger-default">
                                        *
                                    </span>
                                </label>
                                <InputText
                                    value={data.title}
                                    onChange={(e) =>
                                        updateTitle(
                                            lang.code,
                                            e.target.value.slice(0, TITLE_MAX)
                                        )
                                    }
                                    className="w-full h-12"
                                />
                                <div className="text-xs text-right mt-1">
                                    {data.title.length}/{TITLE_MAX}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {t("announcements:modal.textLabel")}
                                    <span className="text-danger-default">
                                        *
                                    </span>
                                </label>
                                <AnnouncementQuillEditor
                                    value={data.text}
                                    maxChars={TEXT_MAX}
                                    onChange={(html, textLength) =>
                                        updateText(lang.code, html, textLength)
                                    }
                                />
                                <div className="text-xs text-right text-surface-muted mt-1">
                                    {data.textLength}/{TEXT_MAX}
                                </div>
                            </div>

                            <div className="flex justify-end mt-2">
                                <Button
                                    label={t("announcements:modal.preview")}
                                    icon={PrimeIcons.EYE}
                                    outlined
                                    size="small"
                                    disabled={!isFilled}
                                    onClick={() => handlePreview(data)}
                                />
                            </div>
                        </AccordionTab>
                    );
                })}
            </div>
        </Modal>
    );
};

export default AnnouncementModal;
